"use client";

import { FC, useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCookies } from "react-cookie";

import { IStoryBlokComponent } from "apps/website/components/page/api.types";
import { useNewFormsServiceStore } from "@./state";
import { useAPI } from "apps/website/hooks/useAPI";
import { useAuth, useDatadog } from "@auth/client-sdk-react";
import { IDiscountApplicationCustomerInfo } from "@discounts/types";
import { DisplayState } from "@/constants/state";
import {
  getActionTrackingInfo,
} from "apps/website/utils/tracking/getActionTrackingInfo";

import { IStoryBlokStickyCTA, StoryBlokStickyCTA } from "../StoryBlokStickyCTA";
import {
  getCancellationReasonForMealPlan,
} from "../helpers/getCancellationReason";

interface IStoryBlokFormCancellationFlowRouter {
  flowId: string;
  disabled?: boolean;
}

export const StoryBlokFormCancellationFlowRouter: FC<IStoryBlokFormCancellationFlowRouter> = ({ flowId }) => {
  const router = useRouter();
  const { getActiveFieldValue } = useNewFormsServiceStore();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  const datadog = useDatadog();
  const discountCode = "WB24CF30";
  const [ buttonState, setButtonState ] = useState<DisplayState>(DisplayState.READY);

  const formsActionsAPI = useAPI().FormActions;
  const [ cookies ] = useCookies([ "_fbp", "_fbc" ]);

  const api = useAPI();
  const { accessToken } = useAuth();

  const [ formState, setFormState ] = useState<string>(
    "LOADING",
  );

  const getAppliedDiscounts = async () => {

    try {
      const { data } = await api.Discounts.getAllDiscountApplications();

      if (!data) {
        datadog.logger.error("Error fetching user's discount applications");
        setFormState("DOES_NOT_HAVE_DISCOUNT");
      }

      const hasDiscount =
      data.find((discountInfo: IDiscountApplicationCustomerInfo) => discountInfo.discount.code === discountCode);

      if (hasDiscount) {
        setFormState("ALREADY_HAS_DISCOUNT");
      } else {
        setFormState("DOES_NOT_HAVE_DISCOUNT");
      }

    } catch (error) {
      datadog.logger.error(`Error fetching user's discount applications: ${error}`);
      setFormState("DOES_NOT_HAVE_DISCOUNT");
    }

  };

  useEffect(() => {
    void getAppliedDiscounts();
  }, [ accessToken ]);

  const getFlowContinuePath = (
    primaryReason: string | undefined = undefined,
    secondaryReason: string | undefined = undefined,
    tertiaryReason: string | undefined = undefined,
  ): string => {
    // If passed away, cancel subscription and redirect to dedicated page
    if (primaryReason === "PASSED_AWAY") {
      return "";
    }

    if (primaryReason === "NOT_EATING") {
      if (posthog.getFeatureFlag("discount-during-cancellation") === "test" && (formState === "DOES_NOT_HAVE_DISCOUNT")) {
        return "/forms/cancellation-discount";
      }
      if (secondaryReason === "NOT_ENTHUSIASTIC_ENOUGH") return "/forms/cancellation-cat-not-eating-not-enthusiastic";
      if (secondaryReason === "LEFTOVER_FOOD") return "/forms/cancellation-cat-not-eating-leftovers";
      if (secondaryReason === "WONT_EAT_AT_ALL") return "/forms/cancellation-cat-not-eating-not-at-all";
      if (secondaryReason === "WENT_OFF_FOOD") return "/forms/cancellation-cat-not-eating-gone-off-food";
      if (secondaryReason === "NEW_FOOD_24") return "/forms/cancellation-cat-not-eating-gone-off-food";
      if (secondaryReason === "LIKED_SOME_RECIPES") return "/forms/cancellation-cat-not-eating-eats-some-recipes";
    }

    if (primaryReason === "HEALTH_REASONS") {
      if (secondaryReason === "VOMITING_OR_LOOSE_STOOLS") return "/forms/cancellation-health-changed-upset-stomach";
      if (secondaryReason === "REQUIRE_DIFFERENT_KIND_OF_FOOD") return "/forms/cancellation-health-changed-required-different-food";
    }

    if (primaryReason === "NEW_PACKAGING") return "/forms/cancellation-packaging";

    if (primaryReason === "LIMITED_FREEZER_SPACE") {
      if (secondaryReason === "ACCUMULATION_OVER_TIME") return "/forms/cancellation-freezer-space-accumulation";
      if (secondaryReason === "NO_MORE_ROOM") return "/forms/cancellation-freezer-space-lack-of";
    }

    if (primaryReason === "PRODUCT_CHANGE_24") return "/forms/cancellation-unhappy-with-changes-in-the-product";

    if (primaryReason === "UNHAPPY_WITH_SERVICE") {
      if (secondaryReason === "UNHAPPY_SERVICE") return "/forms/cancellation-unhappy-with-service";
      if (secondaryReason === "UNHAPPY_DPD") return "/forms/cancellation-unhappy-with-service";
      if (secondaryReason === "UNHAPPY_QUALITY") return "/forms/cancellation-unhappy-with-service";
      if (secondaryReason === "UNHAPPY_OTHER") {
        if (tertiaryReason?.length) return "/forms/cancellation-unhappy-with-service";
      }
    }

    if (primaryReason === "TOO_EXPENSIVE") {
      if (posthog.getFeatureFlag("discount-during-cancellation") === "test" && (formState === "DOES_NOT_HAVE_DISCOUNT")) {
        return "/forms/cancellation-discount";
      }
      if (secondaryReason === "OVER_MY_BUDGET") return "/forms/cancellation-too-expensive-over-my-budget";
      if (secondaryReason === "OTHER_BRANDS_CHEAPER") return "/forms/cancellation-too-expensive-default";
      if (secondaryReason === "EXPENSIVE_WORTH") return "/forms/cancellation-too-expensive-default";
      if (secondaryReason === "DELIVERYCOST") return "/forms/cancellation-too-expensive-delivery-charge";
    }

    if (primaryReason === "COMING_BACK") {
      if (secondaryReason === "GOING_ON_HOLIDAY") return "/forms/cancellation-coming-back-holiday";
      if (secondaryReason === "HAVE_ENOUGH_FOR_NOW") return "/forms/cancellation-coming-back-freezer-stocked";
      if (secondaryReason === "COMINGBACK_MULTIFEEDING") return "/forms/cancellation-coming-back-multi-feeding";
    }

    if (primaryReason === "SUB_DOESNT_WORK") {
      if (posthog.getFeatureFlag("discount-during-cancellation") === "test" && (formState === "DOES_NOT_HAVE_DISCOUNT")) {
        return "/forms/cancellation-discount";
      }
      if (secondaryReason === "WANT_TO_TRY_FIRST_BOX") return "/forms/cancellation-sub-doesn-t-work-try-before-commiting";
      if (secondaryReason === "PREFER_TO_BUY_WHEN_I_NEED_IT") return "/forms/cancellation-sub-doesn-t-work-buy-when-needed";
      if (secondaryReason === "NEED_LESS_FOOD_THAN_SUBSCRIPTION") return "/forms/cancellation-sub-doesn-t-work-need-less-food";
    }

    return "";
  };

  const params = (): string => {
    const cancellationReasons = {
      cancellationPrimary: getActiveFieldValue(flowId, "cancellationPrimary")?.data.submitValue,
      cancellationSecondary: getActiveFieldValue(flowId, "cancellationSecondary")?.data.submitValue,
      cancellationTertiary: getActiveFieldValue(flowId, "cancellationTertiary")?.data.submitValue,
      catName: getActiveFieldValue(flowId, "catName")?.data.submitValue,
      catSex: getActiveFieldValue(flowId, "catSex")?.data.submitValue,
      subscriptionId: getActiveFieldValue(flowId, "freshMealPlanId")?.data.submitValue,
      catId: searchParams.get("catId"),
      cancellationReason: getCancellationReasonForMealPlan(
        getActiveFieldValue(flowId, "cancellationPrimary")?.data.submitValue as string || "",
        getActiveFieldValue(flowId, "cancellationSecondary")?.data.submitValue as string || "",
      ),
    };

    const queryString: string[] = [];

    Object.entries({
      ...cancellationReasons,
      returnToPath: `/forms/cancellation?catId=${searchParams.get("catId")}`,
    }).forEach(([ key, value ]) => {
      if (value) {
        if (key === "catSex") queryString.push(`cat_sex=${value}`);
        queryString.push(`${key}=${value}`);
      }

    });

    return `?${queryString.join("&")}`;

  };

  const handlePassedAwayCancellation = async (primaryReason: string): Promise<string> => {
    try {
      setButtonState(DisplayState.PROCESSING);
      const featureFlags = posthog.featureFlags.getFlagVariants();
      const catName = getActiveFieldValue(flowId, "catName", "0");
      const action = "cancelFreshPlan";

      const submissionFieldValues = new Map();
      submissionFieldValues.set("primaryReason", {
        value: primaryReason,
        linkingId: "0",
      });
      submissionFieldValues.set("subscriptionId", {
        value: Number(getActiveFieldValue(flowId, "freshMealPlanId", "0")?.data.submitValue),
        linkingId: "0",
      });

      const result = await formsActionsAPI.performAction<true>(
        action,
        submissionFieldValues,
        "FRESH",
        flowId,
        undefined,
        featureFlags,
        getActionTrackingInfo(cookies),
        datadog.logger,
      );

      if (result.success) {
        setButtonState(DisplayState.COMPLETE);
        router.push(`/forms/cancellation-passed-away?catName=${catName?.data.submitValue}&catId=${searchParams.get("catId")}`);
      }
      setButtonState(DisplayState.ERROR);
      datadog.logger.error("Could not cancel subscription for passed away cat", {
        action,
        submissionFieldValues,
        featureFlags,
        catName,
        flowId,
        result,
      });
      return "";

    } catch (error) {
      setButtonState(DisplayState.ERROR);
      datadog.logger.error("Error cancelling subscription for passed away cat", { error });
      return "";
    }
  };
  const link = {
    url: `${getFlowContinuePath(
      getActiveFieldValue(flowId, "cancellationPrimary")?.data.submitValue as string || "",
      getActiveFieldValue(flowId, "cancellationSecondary")?.data.submitValue as string || "",
      getActiveFieldValue(flowId, "cancellationTertiary")?.data.submitValue as string || "",
    )}${params()}`,
  };

  // Modify the link object to handle the async nature of getFlowContinuePath
  const handleClick = async () => {
    const primaryReason = getActiveFieldValue(flowId, "cancellationPrimary")?.data.submitValue as string;

    if (primaryReason !== "PASSED_AWAY") {
      return undefined;
    }

    await handlePassedAwayCancellation(primaryReason);
  };

  const formComponent: IStoryBlokComponent<IStoryBlokStickyCTA> = {
    blok: {
      text: "string",
      cta: [ {
        _uid: "cancellationFlowRouter",
        name: "Continue",
        color: "default",
        design: "default",
        keep_query_params: true,
        link,
        test_id: "cancellationFlowRouterBtn",
      } ],
      onClick: handleClick,
      show_trustpilot_rating: false,
      disabled: buttonState === DisplayState.PROCESSING,
    },
  };

  return (

    <>
      { formState === "LOADING" ? null : <StoryBlokStickyCTA {...formComponent} /> }
    </>
  );
};
